import React from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';

const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '24px'
};

// Default center for Lagos, Nigeria
const lagosCenter = {
  lat: 6.5244,
  lng: 3.3792
};

const mockMarkers = [
  { id: 1, position: { lat: 6.5244, lng: 3.3792 }, title: 'Lagos Island - Heavy Traffic', type: 'traffic' },
  { id: 2, position: { lat: 6.4281, lng: 3.4219 }, title: 'Victoria Island - Event Nearby', type: 'event' },
  { id: 3, position: { lat: 9.0765, lng: 7.3986 }, title: 'Abuja Central - Clear Roads', type: 'traffic' },
];

export function GoogleMapView() {
  // Replace 'YOUR_API_KEY_HERE' with your real Google Maps API Key
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'YOUR_API_KEY_HERE'
  });

  const [selectedMarker, setSelectedMarker] = React.useState<any>(null);

  if (loadError) {
    return (
      <div className="w-full h-full bg-zinc-100 rounded-[24px] flex flex-col items-center justify-center p-8 text-center border-2 border-dashed border-zinc-300">
        <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
        </div>
        <h3 className="font-bold text-zinc-900 mb-2">Google Maps Load Error</h3>
        <p className="text-xs text-zinc-500 max-w-[200px]">
          Please check your API key in <code className="bg-zinc-200 px-1 rounded">GoogleMapView.tsx</code>.
        </p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full bg-zinc-50 rounded-[24px] flex items-center justify-center animate-pulse border border-zinc-200">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Loading Sentlo Map...</span>
        </div>
      </div>
    );
  }

  // If the user hasn't added a key, we show a friendly placeholder that looks like a map
  const hasNoKey = 'YOUR_API_KEY_HERE' === 'YOUR_API_KEY_HERE';

  if (hasNoKey) {
    return (
      <div className="w-full h-full bg-zinc-100 rounded-[24px] relative overflow-hidden group border border-zinc-200 shadow-inner">
        {/* Mock Map Background using CSS patterns or an image */}
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px]" />
        
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
          <div className="bg-white p-6 rounded-3xl shadow-xl border border-zinc-100 max-w-[280px] transform group-hover:scale-105 transition-transform">
            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
            </div>
            <h3 className="text-sm font-black text-zinc-900 uppercase tracking-tight mb-2">Real-time Map Ready</h3>
            <p className="text-[10px] text-zinc-500 leading-relaxed mb-6 font-medium">
              To activate the interactive Google Map for Lagos & Abuja, please insert your <span className="text-green-600 font-bold">Google Maps API Key</span> in <code className="bg-zinc-50 border border-zinc-200 px-1 py-0.5 rounded text-[9px]">/src/app/components/GoogleMapView.tsx</code>.
            </p>
            <a 
              href="https://console.cloud.google.com/google/maps-apis/credentials" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block w-full bg-zinc-900 text-white text-[10px] font-black uppercase tracking-widest py-3 rounded-xl hover:bg-zinc-800 transition-all"
            >
              Get API Key
            </a>
          </div>
        </div>

        {/* Floating Mock UI Elements */}
        <div className="absolute top-4 left-4 right-4 flex justify-between pointer-events-none">
          <div className="bg-white/90 backdrop-blur-md px-3 py-2 rounded-xl border border-zinc-200 shadow-sm flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-[9px] font-bold text-zinc-600 uppercase">Live Traffic View</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={lagosCenter}
      zoom={12}
      options={{
        styles: [
          {
            "featureType": "all",
            "elementType": "labels.text.fill",
            "stylers": [{ "color": "#1f1f1f" }]
          },
          {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [{ "color": "#e9e9e9" }]
          }
        ],
        disableDefaultUI: true,
        zoomControl: true,
      }}
    >
      {mockMarkers.map((marker) => (
        <Marker
          key={marker.id}
          position={marker.position}
          onClick={() => setSelectedMarker(marker)}
          icon={{
            url: marker.type === 'traffic' ? 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' : 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
          }}
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={selectedMarker.position}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <div className="p-2">
            <h4 className="text-xs font-bold text-zinc-900">{selectedMarker.title}</h4>
            <p className="text-[10px] text-zinc-500">Sentlo Live Update</p>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
}
